import { ENGINEERING_DATA } from "./datasets.js";

/** PipeKit engineering-core — stable calculation schemas.
 *  Each calculator declares a typed input shape and output shape, plus runtime
 *  validators so untrusted URL/localstate/IndexedDB data can be validated before
 *  use. Schemas are versioned: records store the schema version they satisfy. */

export interface CalcInput { /* tagged union per tool — see tools below */ }
export interface CalcOutput { result: number; unit: string; note?: string }

/** A validated, typed calculator input. */
export interface PipeSizingInput {
  flowM3s: number;        // canonical
  maxVelocityMps: number; // canonical
  material: string;
  frictionLimitF100: number | null; // US display criterion (null = none)
  idIn: number;           // canonical (m) internal diameter
}
export interface PipeSizingOutput {
  minDiameterM: number;
  velocityMps: number;
  status: "ok" | "warn" | "bad";
  selectedIdM: number | null;
}

export interface DrainSelectInput { totalDFU: number; cpcMode: boolean }
export interface DrainSelectOutput { branchSize: string | null; stackSize: string | null }

export interface VentInput { servedDrainIn: string; lengthM: number }
export interface VentOutput { size: string | null; simplified: true }

export interface StormInput { roofAreaM2: number; rateMps: number; datasetId: "storm.ipc.2024" | "storm.cpc.2022-legacy" }
export interface StormOutput { leaderSize: string | null; runoffM3s: number; datasetId: StormInput["datasetId"] }

export interface HeaterInput { gpmCanon: number; deltaK: number }
export interface HeaterOutput { watts: number; btuHr: number }

export interface PressureDropInput {
  qCanonical: number; C: number; idM: number; lenM: number;
}
export interface PressureDropOutput { pascals: number; velMps: number }

export interface ExpTankInput { volL: number; tinC: number; toutC: number; supplyPa: number }
export interface ExpTankOutput { acceptanceL: number }

/* Static head (elevation) -> pressure. */
export interface HeadInput { elevationM: number; fluidDensityKgM3?: number }
export interface HeadOutput { pressurePa: number; headM: number }

/* Mixed (tempered) water temperature. */
export interface MixInput { hotC: number; coldC: number; hotFlowM3s: number; coldFlowM3s: number }
export interface MixOutput { tempC: number; hotFrac: number }

/* Water volume in a pipe run. */
export interface VolumeInput { idM: number; lengthM: number }
export interface VolumeOutput { volumeM3: number; waterMassKg: number }

/* Pump total dynamic head. */
export interface PumpInput {
  staticSuctionM: number;   // lift from suction water to pump OR static discharge head above pump
  staticDischargeM: number; // elevation lift from pump to high point
  frictionHeadM: number;    // total friction in suction+discharge
  pressureHeadM: number;    // required terminal pressure as head (m)
}
export interface PumpOutput { tdhM: number }

/* Manning's open-channel flow. */
export interface ManningInput {
  slope: number;          // hydraulic slope (unitless, e.g. 0.01)
  bottomWidthM: number;   // rectangular channel bottom width
  depthM: number;         // flow depth
  manningN: number;       // roughness coefficient
}
export interface ManningOutput { flowM3s: number; velocityMps: number }

/* Water hammer (Joukowsky) — pressure spike from sudden velocity change. */
export interface WaterHammerInput { fluidVelMps: number; waveSpeedMps?: number }
export interface WaterHammerOutput { surgePa: number; surgeVelMps: number }

/* Pipe thermal expansion. */
export interface ThermalExpansionInput {
  lengthM: number;         // original length
  deltaTempK: number;      // temperature change
  coeffPerK: number;       // linear thermal expansion coefficient (m/m·K)
}
export interface ThermalExpansionOutput { deltaLengthM: number }

/* Gas: load (sum + diversity) & low-pressure sizing (Schafer-style, SG-corrected). */
export interface GasAppliance { name: string; btuHr: number; diverge: boolean }
export interface GasLoadRequest { appliances: GasAppliance[] }
export interface GasLoadOutput { totalBtuHr: number }

/* Hanger spacing table lookup (water/steam) — common IPC/sheet values (in -> ft). */
export interface HangerSpacingInput { nominalIn: number }
export interface HangerSpacingOutput { spacingFt: number; spanIn: number }
export const HANGER_SPACING:Record<number,number>=ENGINEERING_DATA.hangerSpacing;

/* Medical gas pipe sizing — NFPA 99 velocity + pressure-drop check (simplified). */
export type MedGasType = "oxygen" | "nitrous" | "air" | "vacuum" | "nitrogen" | "co2";
export const MED_GAS_PROPS:Record<MedGasType,{label:string;sg:number;vMaxMps:number;supplyPsi:number}>=ENGINEERING_DATA.medGasProperties;
export interface MedGasInput { flowLpm: number; lengthM: number; gas: MedGasType; allowDropKpa?: number }
export interface MedGasOutput { requiredIdM: number; selectedIdIn: number | null; velocityMps: number; estDropKpa: number }

/** Toolkit id ↔ schema. Kept as a typed map for runtime + declared shapes. */
export const TOOLS = [
  "pipe", "press", "supply", "drain", "sewer", "vent", "storm", "hstorm", "manning", "heater", "exptank", "conv",
  "head", "mix", "vol", "pump", "hammer", "thermal", "gassize", "hanger", "medgas",
] as const;
export type Tool = (typeof TOOLS)[number];

export const TOOL_SCHEMA_VERSION: Record<Tool, string> = {
  pipe: "2.0.0", press: "2.0.0", supply: "2.0.0", drain: "2.0.0", sewer: "2.0.0",
  vent: "2.0.0", storm: "2.0.0", hstorm: "2.0.0", manning: "2.0.0", heater: "2.0.0", exptank: "2.0.0", conv: "2.0.0",
  head: "2.0.0", mix: "2.0.0", vol: "2.0.0", pump: "2.0.0",
  hammer: "2.0.0", thermal: "2.0.0", gassize: "2.0.0", hanger: "2.0.0", medgas: "2.0.0",
};

/** Legacy record retained for backward-compatible reads. */
export interface LegacyCalcRecord {
  schemaVersion: string;
  kind: Tool;
  inputs: Record<string, number | string | boolean | null>;
  output: { value: number; unit: string; text: string };
  datasets: string[];
  engineVersion: string;
  codeEdition: string;
  createdAt: number;
}

export type ReviewStatus = "draft" | "ready_for_review" | "reviewed" | "superseded";
/** Immutable CalculationRecord v2 used by local, IndexedDB and cloud storage. */
export interface CalcRecordV2 {
  schema_version: "calc.v2";
  type: Tool;
  tool_version: string;
  project_id: string;
  author_id: string;
  normalized_inputs: Record<string, unknown>;
  display_inputs: Record<string, unknown>;
  structured_outputs: Record<string, unknown>;
  assumptions: unknown[];
  calculation_steps: unknown[];
  engineering_basis: unknown[];
  warnings: unknown[];
  dataset_fingerprints: string[];
  dataset_snapshots: Record<string, unknown>[];
  reference_snapshot: Record<string, unknown>;
  engine_version: string;
  app_build: string;
  code: string;
  edition: string;
  revision: number;
  supersedes_id?: string | null;
  review_status: ReviewStatus;
  reviewed_by?: string | null;
  reviewed_at?: number | string | null;
  createdAt?: number;
  created_at?: string;
}
export type CalcRecord = LegacyCalcRecord | CalcRecordV2;
const nonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;
const nonEmptyPlainObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value) && Object.keys(value).length > 0;
const nonEmptyArray = (value: unknown): value is unknown[] => Array.isArray(value) && value.length > 0;

/** Guard: validate untrusted legacy or v2 records before import/use. */
export function isCalcRecord(x: unknown): x is CalcRecord {
  if (!x || typeof x !== "object") return false;
  const raw = x as Record<string, unknown>;
  if (raw.schema_version === "calc.v2") {
    const statuses = ["draft", "ready_for_review", "reviewed", "superseded"];
    const normalized=raw.normalized_inputs as Record<string,unknown>;
    const outputs=raw.structured_outputs as Record<string,unknown>;
    const primary=outputs?.primary as Record<string,unknown>;
    const reference=raw.reference_snapshot as Record<string,unknown>;
    return typeof raw.type === "string" && (TOOLS as readonly string[]).includes(raw.type)
      && nonEmptyString(raw.tool_version) && nonEmptyString(raw.project_id) && nonEmptyString(raw.author_id)
      && nonEmptyPlainObject(raw.normalized_inputs) && nonEmptyPlainObject(raw.display_inputs) && nonEmptyPlainObject(raw.structured_outputs)
      && nonEmptyArray(raw.assumptions) && nonEmptyArray(raw.calculation_steps)
      && nonEmptyArray(raw.engineering_basis) && nonEmptyArray(raw.warnings)
      && nonEmptyArray(raw.dataset_fingerprints) && raw.dataset_fingerprints.every(nonEmptyString)
      && nonEmptyArray(raw.dataset_snapshots) && raw.dataset_snapshots.every(nonEmptyPlainObject)
      && nonEmptyPlainObject(raw.reference_snapshot)
      && nonEmptyString(raw.engine_version) && nonEmptyString(raw.app_build)
      && nonEmptyString(raw.code) && nonEmptyString(raw.edition)
      && normalized.unit_system === "SI-canonical" && normalized.code === raw.code && normalized.edition === raw.edition
      && outputs.tool === raw.type && outputs.code === raw.code && outputs.edition === raw.edition && nonEmptyString(outputs.metric)
      && nonEmptyPlainObject(primary) && typeof primary.value === "number" && Number.isFinite(primary.value) && nonEmptyString(primary.unit) && nonEmptyString(primary.text)
      && reference.tool === raw.type && reference.code === raw.code && reference.edition === raw.edition
      && Number.isInteger(raw.revision) && Number(raw.revision) > 0
      && typeof raw.review_status === "string" && statuses.includes(raw.review_status)
      && (Number(raw.revision) === 1 ? raw.supersedes_id == null : (typeof raw.supersedes_id === "string" && raw.supersedes_id.length > 0))
      && (raw.review_status !== "reviewed" || (typeof raw.reviewed_by === "string" && raw.reviewed_by.length > 0 && (typeof raw.reviewed_at === "number" || typeof raw.reviewed_at === "string")))
      && (typeof raw.createdAt === "number" || typeof raw.created_at === "string");
  }
  const legacy = x as unknown as LegacyCalcRecord;
  return typeof legacy.schemaVersion === "string"
    && typeof legacy.kind === "string"
    && legacy.inputs !== null && typeof legacy.inputs === "object"
    && typeof legacy.createdAt === "number"
    && (TOOLS as readonly string[]).includes(legacy.kind);
}

/** Guard: a number within [min,max] (both inclusive) and finite. */
export function validNumber(v: unknown, min = -Infinity, max = Infinity): v is number {
  return typeof v === "number" && isFinite(v) && v >= min && v <= max;
}
/** Validate a raw URL-param record for a tool into typed inputs (best effort). */
export function coerceInputs(kind: Tool, raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === "string" && v.trim() !== "" && !Number.isNaN(Number(v))) {
      out[k] = Number(v);
    } else if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      out[k] = v;
    }
    // drop null/unknown
  }
  return out;
}